import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, Download, Printer, GitBranch, Eraser, Search, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { birdsToCSV } from "@/lib/schedule-utils";
import { useGroups } from "@/context/GroupContext";
import { useI18n } from "@/context/I18nContext";
import { useColumnResize } from "@/lib/useColumnResize";

// Default widths: Ring | Sex | Mut | F-Ring | F-Mut | M-Ring | M-Mut
const DEFAULT_WIDTHS = [110, 80, 140, 80, 130, 80, 130];

function BirdRowItem({ bird, index, onSave, savingIndex, onReset, checked, onCheck }) {
  const { t } = useI18n();
  const [local, setLocal] = useState(bird);
  const dirtyRef = useRef(false);

  useEffect(() => { setLocal(bird); }, [bird]);

  const setField = (patch) => {
    dirtyRef.current = true;
    setLocal((p) => ({ ...p, ...patch }));
  };

  const flush = async () => {
    if (!dirtyRef.current) return;
    dirtyRef.current = false;
    await onSave(index, {
      ring: local.ring || "",
      sex: local.sex || "",
      mutation: local.mutation || "",
      father_ring: local.father_ring || "",
      father_mutation: local.father_mutation || "",
      mother_ring: local.mother_ring || "",
      mother_mutation: local.mother_mutation || "",
    });
  };

  const onSexChange = async (v) => {
    const sex = v === "none" ? "" : v;
    dirtyRef.current = true;
    setLocal((p) => ({ ...p, sex }));
    // Save immediately (no blur for select)
    dirtyRef.current = false;
    await onSave(index, {
      ring: local.ring || "",
      sex,
      mutation: local.mutation || "",
      father_ring: local.father_ring || "",
      father_mutation: local.father_mutation || "",
      mother_ring: local.mother_ring || "",
      mother_mutation: local.mother_mutation || "",
    });
  };

  const isSaving = savingIndex === index;
  const ringTrim = (local.ring || "").trim();

  return (
    <tr data-testid={`bird-row-${index}`}>
      <td className="sel" style={{ width: 30, textAlign: "center" }}>
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onCheck(e.target.checked)}
          style={{ width: 15, height: 15, margin: "0 auto", accentColor: "#6b7245" }}
          data-testid={`select-bird-${index}`}
        />
      </td>
      <td className="rownum">{local.row_no}</td>
      <td>
        <input
          value={local.ring || ""}
          onChange={(e) => setField({ ring: e.target.value })}
          onBlur={flush}
          placeholder={t("birds.ring")}
          data-testid={`bird-ring-${index}`}
        />
      </td>
      <td>
        <select
          value={local.sex || ""}
          onChange={(e) => onSexChange(e.target.value)}
          data-testid={`bird-sex-${index}`}
        >
          <option value="">—</option>
          <option value="male">{`♂ ${t("birds.male")}`}</option>
          <option value="female">{`♀ ${t("birds.female")}`}</option>
        </select>
      </td>
      <td>
        <input
          value={local.mutation || ""}
          onChange={(e) => setField({ mutation: e.target.value })}
          onBlur={flush}
          placeholder={t("birds.mutation")}
          data-testid={`bird-mutation-${index}`}
        />
      </td>
      <td className="male">
        <input
          value={local.father_ring || ""}
          onChange={(e) => setField({ father_ring: e.target.value })}
          onBlur={flush}
          placeholder={t("birds.father_ring")}
          data-testid={`bird-father-ring-${index}`}
        />
      </td>
      <td>
        <input
          value={local.father_mutation || ""}
          onChange={(e) => setField({ father_mutation: e.target.value })}
          onBlur={flush}
          placeholder={t("birds.father_mut")}
          data-testid={`bird-father-mutation-${index}`}
        />
      </td>
      <td className="female">
        <input
          value={local.mother_ring || ""}
          onChange={(e) => setField({ mother_ring: e.target.value })}
          onBlur={flush}
          placeholder={t("birds.mother_ring")}
          data-testid={`bird-mother-ring-${index}`}
        />
      </td>
      <td>
        <input
          value={local.mother_mutation || ""}
          onChange={(e) => setField({ mother_mutation: e.target.value })}
          onBlur={flush}
          placeholder={t("birds.mother_mut")}
          data-testid={`bird-mother-mutation-${index}`}
        />
      </td>
      <td className="reset action">
        {isSaving ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin text-espresso/40 mx-auto" />
        ) : (
          <div className="flex items-center justify-center gap-0.5">
            {ringTrim && (
              <Link
                to={`/pedigree/${encodeURIComponent(ringTrim)}`}
                className="p-1 text-olive hover:text-olive/70"
                title={t("birds.pedigree")}
                data-testid={`bird-pedigree-link-${index}`}
              >
                <GitBranch className="w-3 h-3" />
              </Link>
            )}
            <button
              onClick={() => onReset(index)}
              className="p-1 text-espresso/30 hover:text-danger"
              title={t("common.reset")}
              data-testid={`reset-bird-${index}`}
            >
              <Eraser className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </td>
    </tr>
  );
}

function ResizableTh({ index, width, startResize, startResizeTouch, children }) {
  return (
    <th style={{ width }}>
      {children}
      <span
        className="resize-handle"
        onMouseDown={(e) => startResize(e, index)}
        onTouchStart={(e) => startResizeTouch(e, index)}
      />
    </th>
  );
}

export default function BirdsPage() {
  const { activeGroup } = useGroups();
  const { t } = useI18n();
  const [birds, setBirds] = useState([]);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingIndex, setSavingIndex] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const editingRef = useRef(false);
  const { widths, startResize, startResizeTouch } = useColumnResize("xls-birds", DEFAULT_WIDTHS);

  const cacheKey = activeGroup?.id ? `birds:${activeGroup.id}` : null;

  const load = useCallback(async () => {
    if (!activeGroup?.id) return;
    try {
      const cached = cacheKey && localStorage.getItem(cacheKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed.birds)) {
          setBirds(parsed.birds);
          setUpdatedAt(parsed.birds_updated_at);
          setLoading(false);
        }
      }
    } catch (_e) { void _e; }
    try {
      const { data } = await api.get("/schedule/birds");
      setBirds(data.birds || []);
      setUpdatedAt(data.birds_updated_at);
      if (cacheKey) {
        try { localStorage.setItem(cacheKey, JSON.stringify(data)); } catch (_e) { void _e; }
      }
    } catch {
      if (!cacheKey || !localStorage.getItem(cacheKey)) {
        toast.error("Offline — no cached data yet");
      }
    } finally {
      setLoading(false);
    }
  }, [activeGroup?.id, cacheKey]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load, activeGroup?.id]);

  useEffect(() => {
    const id = setInterval(async () => {
      if (editingRef.current) return;
      try {
        const { data } = await api.get("/schedule/sync");
        if (data.birds_updated_at && data.birds_updated_at !== updatedAt) {
          const res = await api.get("/schedule/birds");
          setBirds(res.data.birds || []);
          setUpdatedAt(res.data.birds_updated_at);
        }
      } catch (_e) { void _e; }
    }, 6000);
    return () => clearInterval(id);
  }, [updatedAt]);

  const onSave = async (index, payload) => {
    setSavingIndex(index);
    editingRef.current = true;
    try {
      const { data } = await api.put(`/schedule/birds/${index}`, payload);
      setUpdatedAt(data.updated_at);
      setBirds((arr) => {
        const copy = [...arr];
        copy[index] = { ...copy[index], ...payload };
        return copy;
      });
    } catch {
      toast.error("Save failed");
    } finally {
      setSavingIndex(null);
      setTimeout(() => { editingRef.current = false; }, 500);
    }
  };

  const doReset = async () => {
    if (resetTarget == null) return;
    const idx = resetTarget;
    setResetTarget(null);
    const empty = {
      ring: "",
      sex: "",
      mutation: "",
      father_ring: "",
      father_mutation: "",
      mother_ring: "",
      mother_mutation: "",
    };
    await onSave(idx, empty);
    try {
      const { data } = await api.get("/schedule/birds");
      setBirds(data.birds || []);
      setUpdatedAt(data.birds_updated_at);
    } catch (_e) { void _e; }
    toast.success(t("common.reset"));
  };

  const [jumpNo, setJumpNo] = useState("");
  const tableWrapRef = useRef(null);
  const [selected, setSelected] = useState({});
  const [bulkCount, setBulkCount] = useState("10");
  const selCount = Object.values(selected).filter(Boolean).length;

  const bulkAdd = async () => {
    try {
      await api.post("/schedule/birds/bulk-add", { count: parseInt(bulkCount, 10) });
      await load();
      toast.success(t("bulk.added"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const deleteSelected = async () => {
    if (!window.confirm(t("bulk.delete_confirm"))) return;
    const indices = Object.keys(selected).filter((k) => selected[k]).map(Number);
    setSelected({});
    try {
      await api.post("/schedule/birds/delete-rows", { indices });
      await load();
      toast.success(t("bulk.deleted"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };
  const jumpTo = (value) => {
    setJumpNo(value);
    const n = parseInt(value, 10);
    if (!n || n < 1 || n > birds.length || !tableWrapRef.current) return;
    const row = tableWrapRef.current.querySelector(`[data-testid="bird-row-${n - 1}"]`);
    if (!row) return;
    row.scrollIntoView({ block: "center", behavior: "smooth" });
    row.style.transition = "background-color 0.3s";
    row.style.backgroundColor = "rgba(180, 190, 130, 0.35)";
    setTimeout(() => { row.style.backgroundColor = ""; }, 1600);
  };

  return (
    <div className="space-y-4" data-testid="birds-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-espresso">
            {t("birds.title")}
          </h1>
          <p className="text-sm text-espresso/70 mt-1">
            {activeGroup?.name ? <span className="text-olive font-semibold">{activeGroup.name}</span> : null}
            {activeGroup?.name ? " · " : ""}
            {t("birds.sub")}
          </p>
        </div>
        <div className="flex items-center gap-2" data-print-hide>
          <div className="flex items-center gap-1.5 bg-white border border-app-border rounded-lg px-2 py-1">
            <Search className="w-3.5 h-3.5 text-espresso/40" />
            <input
              type="number"
              min={1}
              max={birds.length || 300}
              value={jumpNo}
              onChange={(e) => jumpTo(e.target.value)}
              placeholder={t("birds.jump_row")}
              className="w-24 text-sm bg-transparent outline-none placeholder:text-espresso/40"
              data-testid="birds-jump-input"
            />
          </div>
          <div className="flex items-center gap-1.5">
            <select
              value={bulkCount}
              onChange={(e) => setBulkCount(e.target.value)}
              className="text-sm bg-white border border-app-border rounded-lg px-2 py-1.5 outline-none"
              data-testid="birds-bulk-count"
            >
              {[1, 5, 10, 20, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            <Button variant="outline" size="sm" onClick={bulkAdd} data-testid="birds-bulk-add">
              <Plus className="w-4 h-4 mr-1 rtl:mr-0 rtl:ml-1" /> {t("bulk.add")}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={deleteSelected}
              disabled={selCount === 0}
              className="border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-40"
              data-testid="birds-delete-rows"
            >
              <Trash2 className="w-4 h-4 mr-1 rtl:mr-0 rtl:ml-1" /> {t("bulk.delete")}{selCount > 0 ? ` (${selCount})` : ""}
            </Button>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => birdsToCSV(birds, activeGroup?.name)}
            disabled={loading || birds.length === 0}
            data-testid="export-birds-csv"
          >
            <Download className="w-4 h-4 mr-1" /> CSV
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.print()}
            disabled={loading}
            data-testid="print-birds"
          >
            <Printer className="w-4 h-4 mr-1" /> Print
          </Button>
          <div className="hidden sm:flex items-center gap-2 text-xs text-espresso/60 ml-2">
            <span>{t("common.updated")}:</span>
            <span className="font-mono">{updatedAt ? new Date(updatedAt).toLocaleString() : "—"}</span>
          </div>
        </div>
      </div>

      <div className="xls-wrap" ref={tableWrapRef} data-testid="birds-table">
        <table className="xls">
          <thead>
            <tr>
              <th className="sel" style={{ width: 30, textAlign: "center" }}>
                <input
                  type="checkbox"
                  checked={birds.length > 0 && selCount === birds.length}
                  onChange={(e) => setSelected(e.target.checked ? Object.fromEntries(birds.map((_, i) => [i, true])) : {})}
                  title="Select all"
                  style={{ width: 15, height: 15, margin: "0 auto", accentColor: "#6b7245" }}
                  data-testid="select-all-birds"
                />
              </th>
              <th className="rownum">#</th>
              <ResizableTh index={0} width={widths[0]} startResize={startResize} startResizeTouch={startResizeTouch}>
                {t("birds.ring")}
              </ResizableTh>
              <ResizableTh index={1} width={widths[1]} startResize={startResize} startResizeTouch={startResizeTouch}>
                {t("birds.sex")}
              </ResizableTh>
              <ResizableTh index={2} width={widths[2]} startResize={startResize} startResizeTouch={startResizeTouch}>
                {t("birds.mutation")}
              </ResizableTh>
              <ResizableTh index={3} width={widths[3]} startResize={startResize} startResizeTouch={startResizeTouch}>
                {t("birds.father_ring")}
              </ResizableTh>
              <ResizableTh index={4} width={widths[4]} startResize={startResize} startResizeTouch={startResizeTouch}>
                {t("birds.father_mut")}
              </ResizableTh>
              <ResizableTh index={5} width={widths[5]} startResize={startResize} startResizeTouch={startResizeTouch}>
                {t("birds.mother_ring")}
              </ResizableTh>
              <ResizableTh index={6} width={widths[6]} startResize={startResize} startResizeTouch={startResizeTouch}>
                {t("birds.mother_mut")}
              </ResizableTh>
              <th className="reset"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={10} className="p-8 text-center">
                  <Loader2 className="w-5 h-5 animate-spin inline-block text-espresso/60" />
                </td>
              </tr>
            ) : (
              birds.map((b, idx) => (
                <BirdRowItem
                  key={b.row_no}
                  bird={b}
                  index={idx}
                  onSave={onSave}
                  savingIndex={savingIndex}
                  onReset={(i) => setResetTarget(i)}
                  checked={!!selected[idx]}
                  onCheck={(v) => setSelected((s) => ({ ...s, [idx]: v }))}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      <AlertDialog open={resetTarget != null} onOpenChange={(v) => !v && setResetTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("birds.reset_confirm_title")}</AlertDialogTitle>
            <AlertDialogDescription>{t("birds.reset_confirm_desc")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={doReset}
              className="bg-danger hover:bg-danger/90"
              data-testid="confirm-reset-bird"
            >
              {t("common.reset")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
